import { api } from '@goaltree/backend/convex/_generated/api'
import { cn } from '@goaltree/ui/lib/utils'
import { Link } from '@tanstack/react-router'
import { useQuery } from 'convex/react'
import { BellOff, Plus, Ticket, TreeDeciduous } from 'lucide-react'
import { motion } from 'motion/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useCoachPersona } from '@/components/ask-coach'
import { CreateGroveDialog, JoinCodeDialog } from '@/components/groves/grove-dialogs'
import { GroveIcon } from '@/components/groves/grove-icon'
import { timeAgo } from '@/components/groves/grove-utils'
import { StreakFlame } from '@/components/streak-icons'

type Grove = NonNullable<ReturnType<typeof useQuery<typeof api.groves.listMine>>>[number]

type Status = NonNullable<ReturnType<typeof useQuery<typeof api.groves.todayStatus>>>[number]

export function GrovesList() {
  const { t } = useTranslation()
  const groves = useQuery(api.groves.listMine)
  const statuses = useQuery(api.groves.todayStatus)
  const [createOpen, setCreateOpen] = useState(false)
  const [joinOpen, setJoinOpen] = useState(false)

  return (
    <>
      <div className="mb-4 flex min-h-9 items-center justify-between gap-3">
        <p className="min-w-0 text-sm text-muted-foreground">{t('groves.subtitle')}</p>
        {!!groves?.length && (
          <div className="flex shrink-0 gap-2">
            <button
              type="button"
              onClick={() => setJoinOpen(true)}
              aria-label={t('groves.haveCode')}
              className="flex size-9 cursor-pointer items-center justify-center rounded-full border border-border/60 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <Ticket className="size-4" />
            </button>
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="flex h-9 cursor-pointer items-center gap-1.5 rounded-full bg-primary px-4 text-sm font-bold text-primary-foreground transition-colors hover:bg-primary/90 active:scale-[0.99]"
            >
              <Plus className="size-4" strokeWidth={2.5} />
              {t('groves.new')}
            </button>
          </div>
        )}
      </div>
      {groves === undefined ? (
        <ListSkeleton />
      ) : groves.length === 0 ? (
        <EmptyState onCreate={() => setCreateOpen(true)} onJoin={() => setJoinOpen(true)} />
      ) : (
        <div className="divide-y divide-border/40">
          {groves.map((g, i) => (
            <motion.div
              key={g._id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(i, 8) * 0.03 }}
            >
              <GroveRow grove={g} status={statuses?.find((s) => s.groveId === g._id)} />
            </motion.div>
          ))}
        </div>
      )}
      <CreateGroveDialog open={createOpen} onOpenChange={setCreateOpen} />
      <JoinCodeDialog open={joinOpen} onOpenChange={setJoinOpen} />
    </>
  )
}

function CheckInStatus({ status }: { status: Status }) {
  const { t } = useTranslation()
  const { checkedIn: done, memberCount: total } = status
  const waiting = !status.iCheckedIn && done < total
  return (
    <p
      className={cn(
        'truncate text-[11px]',
        waiting ? 'font-semibold text-primary-text' : 'text-muted-foreground',
      )}
    >
      {done >= total
        ? t('groves.today.allIn')
        : waiting
          ? t('groves.today.waitingOnYou', { done, total })
          : t('groves.today.checkedIn', { done, total })}
    </p>
  )
}

function GroveRow({ grove, status }: { grove: Grove; status?: Status }) {
  const { t, i18n } = useTranslation()
  const last = grove.lastMessage
  const preview = !last
    ? t('groves.members', { count: grove.memberCount })
    : last.deleted
      ? t('groves.chat.deleted')
      : last.kind === 'system'
        ? t(`groves.system.${last.body}`, { name: last.authorName ?? '' })
        : last.kind === 'coach'
          ? `${last.authorName}: ${last.body}`
          : `${last.isMine ? t('groves.you') : last.authorName}: ${last.body}`
  return (
    <Link
      to="/today"
      search={{ tab: 'groves', grove: grove._id }}
      className="flex items-center gap-3 px-1 py-3 transition-colors hover:bg-foreground/[0.03]"
    >
      <GroveIcon icon={grove.icon} size="sm" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <p className="truncate text-sm font-bold text-foreground">{grove.name}</p>
          {grove.groveStreak > 0 && (
            <span className="shrink-0 text-xs font-bold text-foreground">
              <StreakFlame /> {grove.groveStreak}
            </span>
          )}
          {grove.muted && <BellOff className="size-3 shrink-0 text-muted-foreground" />}
        </div>
        <p
          className={cn(
            'truncate text-xs',
            grove.unread > 0 ? 'font-semibold text-foreground' : 'text-muted-foreground',
          )}
        >
          {preview}
        </p>
        {status && <CheckInStatus status={status} />}
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        <span className="text-[11px] text-muted-foreground">
          {timeAgo(grove.lastActivityAt, i18n.language)}
        </span>
        {grove.unread > 0 && (
          <span
            className={cn(
              'min-w-5 rounded-full px-1.5 py-px text-center text-[11px] font-bold',
              grove.muted ? 'bg-muted text-muted-foreground' : 'bg-primary text-primary-foreground',
            )}
          >
            {grove.unread > 99 ? '99+' : grove.unread}
          </span>
        )}
      </div>
    </Link>
  )
}

function EmptyState({ onCreate, onJoin }: { onCreate: () => void; onJoin: () => void }) {
  const { t } = useTranslation()
  const { name: coachName } = useCoachPersona()
  return (
    <div className="mt-2 rounded-2xl border border-border/50 bg-foreground/[0.03] px-6 py-10 text-center">
      <TreeDeciduous className="mx-auto size-10 text-primary-text" strokeWidth={1.75} />
      <p className="mt-3 text-lg font-bold text-foreground">{t('groves.empty.title')}</p>
      <p className="mx-auto mt-1.5 max-w-xs text-sm text-muted-foreground">
        {t('groves.empty.desc', { coach: coachName })}
      </p>
      <div className="mt-5 flex flex-col items-center gap-2">
        <button
          type="button"
          onClick={onCreate}
          className="cursor-pointer rounded-full bg-primary px-5 py-2 text-sm font-bold text-primary-foreground transition-all hover:bg-primary/90 active:scale-[0.99]"
        >
          {t('groves.empty.start')}
        </button>
        <button
          type="button"
          onClick={onJoin}
          className="cursor-pointer px-3 py-1.5 text-sm font-semibold text-primary-text transition-colors hover:text-primary-text/80"
        >
          {t('groves.haveCode')}
        </button>
      </div>
    </div>
  )
}

function ListSkeleton() {
  return (
    <div className="divide-y divide-border/40">
      {[52, 68, 44].map((w, i) => (
        <div key={i} className="flex items-center gap-3 px-1 py-3">
          <div className="size-12 shrink-0 animate-pulse rounded-2xl bg-muted/30" />
          <div className="min-w-0 flex-1">
            <div className="h-3.5 animate-pulse rounded bg-muted/30" style={{ width: `${w}%` }} />
            <div className="mt-2 h-2.5 w-3/5 animate-pulse rounded bg-muted/20" />
          </div>
          <div className="h-2.5 w-10 animate-pulse rounded bg-muted/20" />
        </div>
      ))}
    </div>
  )
}
